import { formatImageGenQuotaResetCountdown } from '@littlebird/ui-utils';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import { XStack, YStack } from '@/components/ui/layout/layout';
import { Text } from '@/components/ui/typography';
import { subscriptionStore } from '@/features/subscription/subscription.store';
import { UpgradeBanner } from '@/features/subscription/upgrade-banner';

const TITLE = 'Image generation daily limit reached';

type ImageGenQuotaBadgeProps = {
  messageLimit: number | null;
  nextAllowedTime: string | null;
  userErrorMessage: string | null;
};

export const ImageGenQuotaBadge = observer(
  ({
    messageLimit,
    nextAllowedTime,
    userErrorMessage,
  }: ImageGenQuotaBadgeProps) => {
    const [resetLabel, setResetLabel] = useState('');

    useEffect(() => {
      setResetLabel(formatImageGenQuotaResetCountdown(nextAllowedTime));
      if (!nextAllowedTime) return;
      const interval = setInterval(() => {
        setResetLabel(formatImageGenQuotaResetCountdown(nextAllowedTime));
      }, 60_000);
      return () => clearInterval(interval);
    }, [nextAllowedTime]);

    const description =
      userErrorMessage ??
      (messageLimit !== null
        ? `You've used your ${messageLimit} image${messageLimit === 1 ? '' : 's'} for today.`
        : "You've reached your daily image generation limit.");

    const canUpgrade = subscriptionStore.canUpgrade;

    return (
      <YStack className="w-full px-6 py-2" aria-live="polite">
        <UpgradeBanner.Root>
          <UpgradeBanner.Content>
            <XStack className="items-center gap-2">
              <span aria-hidden="true">🖼️</span>
              <UpgradeBanner.Title>{TITLE}</UpgradeBanner.Title>
            </XStack>
            <UpgradeBanner.Description>{description}</UpgradeBanner.Description>
            <Text size="xs" variant="muted" className="pt-0.5">
              {resetLabel}
            </Text>
          </UpgradeBanner.Content>
          {canUpgrade && (
            <UpgradeBanner.UpgradeButton source="image_gen_quota_exceeded">
              Upgrade
            </UpgradeBanner.UpgradeButton>
          )}
        </UpgradeBanner.Root>
      </YStack>
    );
  }
);
